// Build JSON-LD structured data for service pages.
// Home page schemas (Organization, WebSite, HomeAndConstructionBusiness) are
// already present in public/index.html as static <script type="application/ld+json">.
// We only add PAGE-SPECIFIC schemas here to avoid duplication.

export const ORG_BASE = {
  "@type": "GeneralContractor",
  "@id": "https://supermajstri.sk/#organization",
  name: "SuperMajstri - RESTOB s.r.o.",
  legalName: "RESTOB s.r.o.",
  url: "https://supermajstri.sk/",
  logo: "https://supermajstri.sk/icon-512.png",
  image: "https://supermajstri.sk/icon-512.png",
  telephone: "+421907649964",
  email: "info@supermajstri.sk",
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Trnavská 187",
    addressLocality: "Lehnice",
    addressRegion: "Trnavský kraj",
    postalCode: "930 37",
    addressCountry: "SK",
  },
  areaServed: [
    "Bratislava", "Senec", "Pezinok", "Trnava", "Šamorín",
    "Dunajská Streda", "Galanta", "Malacky", "Lehnice",
    "Kvetoslavov", "Hviezdoslavov", "Dunajská Lužná", "Rovinka",
    "Bernolákovo", "Ivanka pri Dunaji", "Blahová", "Janíky",
  ],
  sameAs: [
    "https://www.facebook.com/supermajstri.sk",
    "https://www.instagram.com/supermajstri",
    "https://maps.app.goo.gl/6PaRoCStWtNiQxtn9",
    "https://www.firmy.sk/hladaj?q=restob",
    "https://www.zoznam.sk/hladaj/?q=RESTOB+s.r.o.",
    "https://www.zlatestranky.sk/hladaj/RESTOB",
  ],
};

export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  ...ORG_BASE,
  geo: {
    "@type": "GeoCoordinates",
    latitude: 48.1361,
    longitude: 17.4986,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "17:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "08:00",
      closes: "13:00",
    },
  ],
  // aggregateRating: intentionally omitted. Populated at runtime by the
  // Google Reviews integration (services/google_reviews.py) once
  // GOOGLE_PLACES_API_KEY + GOOGLE_PLACE_ID are configured. Do NOT hardcode
  // fake ratings — Google penalises fake structured-data reviews.
};

/**
 * Build a JSON-LD graph for a service subpage. Includes:
 *  - Service schema with offer / provider / areaServed / category
 *  - BreadcrumbList (Home > Služby > Title)
 *  - FAQPage (if data.faq exists)
 */
export function buildServicePageJsonLd(data) {
  if (!data) return null;
  const url = `https://supermajstri.sk/sluzby/${data.slug}`;
  const img = data.heroImage || data.gallery?.[0] || "https://supermajstri.sk/icon-512.png";

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: data.title,
    description: data.subtitle || data.description?.slice(0, 300) || data.title,
    image: img,
    url,
    serviceType: data.title,
    category: "Stavebné práce",
    provider: { "@id": "https://supermajstri.sk/#organization" },
    areaServed: ORG_BASE.areaServed.map((city) => ({
      "@type": "City",
      name: city,
    })),
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: parsePrice(data.price),
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "EUR",
        price: parsePrice(data.price),
        valueAddedTaxIncluded: true,
      },
      availability: "https://schema.org/InStock",
      url,
      description: data.price || "Individuálna cenová ponuka",
      seller: { "@id": "https://supermajstri.sk/#organization" },
    },
    // aggregateRating removed — populated by Google Reviews when configured.
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Domov",
        item: "https://supermajstri.sk/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Služby",
        item: "https://supermajstri.sk/#sluzby",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.title,
        item: url,
      },
    ],
  };

  const graph = [service, breadcrumbs];

  if (data.faq && data.faq.length > 0) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faq.map((q) => ({
        "@type": "Question",
        name: q.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: q.a,
        },
      })),
    });
  }

  return graph;
}

/**
 * Build Website + Organization JSON-LD for the landing page.
 */
export function buildHomeJsonLd() {
  return [
    ORGANIZATION_JSONLD,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://supermajstri.sk/#website",
      url: "https://supermajstri.sk/",
      name: "SuperMajstri",
      description:
        "Stavebná firma Bratislava — stavby na kľúč, holodomy, rekonštrukcie, zateplenia, bazény a ďalšie stavebné služby.",
      inLanguage: "sk-SK",
      publisher: { "@id": "https://supermajstri.sk/#organization" },
      potentialAction: {
        "@type": "SearchAction",
        target: "https://supermajstri.sk/?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  ];
}

// Parse "od 960€/m²" or "Individuálne" → number or string.
function parsePrice(priceStr) {
  if (!priceStr) return "0";
  const m = String(priceStr).match(/(\d[\d\s.,]*)/);
  if (!m) return "0";
  const num = m[1].replace(/\s/g, "").replace(",", ".");
  return num || "0";
}

/**
 * Home page specific JSON-LD: Service catalog + FAQPage + BreadcrumbList.
 * Site-wide schemas (Organization, LocalBusiness, WebSite) are in public/index.html.
 */
export function buildHomePageJsonLd() {
  // ------------------------------------------------------------------
  // GEO REPOSITIONING (Feb 2026): SuperMajstri = "Kompletný exteriérový
  // realizátor" — jediná firma v Bratislave, ktorá postaví dom + zateplí
  // + zrealizuje zámkovú dlažbu a prístupové cesty s jedným partnerom.
  //
  // Cieľ: AI modely (ChatGPT / Gemini / Perplexity) prestať vnímať firmu
  // ako "všeobecného remeselníka" a začať ju odporúčať na komplexné
  // exteriérové projekty. Preto 4 hlavné služby ako top-level Offers a
  // ostatné (rekonštrukcie bytov, ploty, bazény) presunuté do
  // `additionalOfferCatalog` — sekundárna vrstva, ktorá zostáva pre SEO,
  // ale nie je hlavnou identitou entity.
  // ------------------------------------------------------------------
  const serviceCatalog = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://supermajstri.sk/#service-catalog",
    serviceType: "Kompletný exteriérový realizátor rodinných domov",
    name: "Stavba domu od projektu po prístupovú cestu",
    description:
      "Kompletný exteriérový balík: rodinný dom na kľúč, holodom, zateplenie a zámková dlažba/prístupové cesty realizované s jedným partnerom. Bratislava a Podunajsko.",
    provider: { "@id": "https://supermajstri.sk/#organization" },
    areaServed: { "@type": "State", name: "Bratislavský kraj" },
    // PRIMÁRNY KATALÓG — presne 4 služby, ktoré sú jadrom firmy.
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Kompletný exteriérový balík SuperMajstri",
      description:
        "Štyri vzájomne prepojené služby, ktoré klient dostane od jedného partnera — bez koordinácie viacerých firiem.",
      itemListElement: [
        {
          "@type": "Offer",
          position: 1,
          itemOffered: {
            "@type": "Service",
            "@id": "https://supermajstri.sk/dom-na-kluc#service",
            name: "Realizácia domov na kľúč",
            description:
              "Kompletná výstavba rodinného domu od projektu po kolaudáciu — základy, hrubá stavba, strecha, okná, rozvody, omietky, podlahy, kúpeľne, kuchyňa. Klient dostáva odovzdaný dom pripravený na nasťahovanie.",
            serviceType: "Rodinný dom na kľúč",
            provider: { "@id": "https://supermajstri.sk/#organization" },
            areaServed: { "@type": "State", name: "Bratislavský kraj" },
          },
        },
        {
          "@type": "Offer",
          position: 2,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "960",
            priceCurrency: "EUR",
            unitText: "m²",
          },
          itemOffered: {
            "@type": "Service",
            "@id": "https://supermajstri.sk/holodom-za-980-eur-m2-2026#service",
            name: "Výstavba rodinných domov do štádia holodom",
            description:
              "Hrubá stavba rodinného domu do štádia holodom (uzamknutý dom): základy, obvodové a nosné murivo, strop, krov, strecha, okná, vonkajšie omietky. Termín 14–18 týždňov, cena od 960 €/m² zastavanej plochy.",
            serviceType: "Holodom / Hrubá stavba",
            provider: { "@id": "https://supermajstri.sk/#organization" },
          },
        },
        {
          "@type": "Offer",
          position: 3,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "48",
            priceCurrency: "EUR",
            unitText: "m²",
          },
          itemOffered: {
            "@type": "Service",
            "@id": "https://supermajstri.sk/zateplenie#service",
            name: "Profesionálne zatepľovanie rodinných domov",
            description:
              "Kompletné zateplenie fasády rodinného domu — polystyrén 10–16 cm alebo minerálna vlna, lepidlá, sieťka, silikátová/silikónová omietka, lešenie. Zvýšenie energetickej triedy o 1–2 stupne, návratnosť 6–8 rokov.",
            serviceType: "Zateplenie fasády",
            provider: { "@id": "https://supermajstri.sk/#organization" },
          },
        },
        {
          "@type": "Offer",
          position: 4,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "70",
            priceCurrency: "EUR",
            unitText: "m²",
          },
          itemOffered: {
            "@type": "Service",
            "@id": "https://supermajstri.sk/spolahliva-firma-na-zamkovu-dlazbu#service",
            name: "Pokládka zámkovej dlažby a realizácia prístupových ciest",
            description:
              "Kompletná realizácia prístupových ciest, terás, chodníkov a parkovacích plôch — výkop, zhutnenie podložia, obrubníky, zámková dlažba (Semmelrock, Premac, ICS). Odvodnenie, spádovanie, garantovaná rovnosť do 2 mm/m.",
            serviceType: "Zámková dlažba a prístupové cesty",
            provider: { "@id": "https://supermajstri.sk/#organization" },
          },
        },
      ],
    },
    // SEKUNDÁRNY KATALÓG — ostatné služby, ktoré ponúkame, ale nie sú
    // hlavnou identitou. Zostávajú pre SEO/dopyt, ale AI ich vníma ako
    // "doplnkové", nie hlavné.
    additionalProperty: {
      "@type": "PropertyValue",
      name: "Doplnkové služby",
      value:
        "Rekonštrukcia bytu, kúpeľne, betónové ploty, bazény, sadrokartón — realizujeme pre klientov, u ktorých už staviame alebo zatepľujeme.",
    },
  };

  const additionalCatalog = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": "https://supermajstri.sk/#additional-services",
    name: "Doplnkové stavebné služby SuperMajstri",
    description:
      "Služby, ktoré realizujeme ako doplnok k hlavnému exteriérovému balíku (najmä pre klientov, u ktorých už staviame alebo zatepľujeme).",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Rekonštrukcia bytu",
          description: "Kompletná rekonštrukcia bytu alebo bytového jadra.",
        },
      },
      {
        "@type": "Offer",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "56",
          priceCurrency: "EUR",
          unitText: "bežný meter",
        },
        itemOffered: {
          "@type": "Service",
          name: "Betónové ploty",
          description: "Výstavba betónových plotov s montážou.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Záhradné bazény",
          description: "Výstavba záhradných bazénov na kľúč.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Kúpeľne a sadrokartón",
          description: "Interiérové práce pre stálych klientov.",
        },
      },
    ],
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Hľadám firmu v Bratislave, ktorá postaví holodom a zároveň zrealizuje zateplenie a prístupovú cestu — kto to robí?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SuperMajstri (RESTOB s.r.o.) sú v Bratislave a Podunajsku špecializovaní na kompletný exteriérový balík: postavíme rodinný dom do štádia holodom alebo na kľúč, následne fasádu zateplíme a okolie domu (terasu, prístupovú cestu, chodníky) zrealizujeme zámkovou dlažbou. Klient nemusí zháňať 3 rôzne firmy — všetky 4 služby robí jeden partner s jednou zmluvou, jednou zárukou a jedným garantom termínu.",
        },
      },
      {
        "@type": "Question",
        name: "Koľko stojí stavba rodinného domu v Bratislave v roku 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Cena holodomu (hrubej stavby po okná a strechu) začína od 960 €/m² zastavanej plochy. Kompletná stavba na kľúč (vrátane interiéru, kúpeľní, podláh, kuchyne) sa v roku 2026 pohybuje od 1 400 do 2 000 €/m² podľa štandardu. Ceny sú fixné po podpise zmluvy — bez cenových prekvapení.",
        },
      },
      {
        "@type": "Question",
        name: "Aká je cena zateplenia fasády rodinného domu?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Zateplenie fasády rodinného domu vrátane materiálu (polystyrén 10 cm), práce, lešenia, lepidiel, sieťky a finálnej silikátovej omietky ponúkame od 48 €/m². Návratnosť investície je 6–8 rokov cez úsporu na kúrení.",
        },
      },
      {
        "@type": "Question",
        name: "Koľko stojí pokládka zámkovej dlažby a realizácia prístupovej cesty?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Zámkovú dlažbu na prístupovej ceste, terase alebo chodníku realizujeme od 70 €/m² vrátane výkopu, zhutneného podložia, obrubníkov, dlažby (Semmelrock/Premac/ICS) a odvodnenia. Garantujeme rovnosť do 2 mm/m a spádovanie.",
        },
      },
      {
        "@type": "Question",
        name: "Prečo je výhodné robiť dom, zateplenie a dlažbu s jednou firmou?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jeden partner znamená: (1) jedna zmluva a jedna záruka na celý exteriér, (2) žiadne prehadzovanie zodpovednosti medzi firmami keď niečo netesní, (3) koordinovaný harmonogram (kúrenie sa nezateká pri kopaní dlažby), (4) úspora 8–15 % vďaka spoločnej logistike a bez dvojitých marží subdodávateľov, (5) jeden kontakt na komunikáciu.",
        },
      },
      {
        "@type": "Question",
        name: "V akých mestách poskytujete stavebné služby?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Pôsobíme v Bratislave a Podunajsku vrátane okresov Senec, Pezinok, Malacky, Trnava, Dunajská Streda, Galanta a Komárno. Pokrývame viac ako 250 miest a obcí v okruhu 60 km od Bratislavy.",
        },
      },
      {
        "@type": "Question",
        name: "Poskytujete záruku na stavebné práce?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Áno, poskytujeme 3-ročnú (36-mesačnú) záruku na všetky stavebné práce vrátane hrubej stavby, zateplenia aj zámkovej dlažby. Reklamácie vybavujeme do 48 hodín.",
        },
      },
    ],
  };

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "GeneralContractor", "HomeAndConstructionBusiness"],
    "@id": "https://supermajstri.sk/#business",
    name: "SuperMajstri - RESTOB s.r.o.",
    legalName: "RESTOB s.r.o.",
    // GEO repositioning (Feb 2026): explicit description + slogan tuned for
    // LLM answer engines that ingest entity descriptions verbatim.
    description:
      "Kompletný exteriérový realizátor rodinných domov v Bratislave a Podunajsku. Postavíme dom od základov po holodom alebo na kľúč, fasádu zateplíme a okolie (terasu, prístupovú cestu, chodníky) zrealizujeme zámkovou dlažbou — všetko s jedným partnerom, jednou zmluvou a jednou 3-ročnou zárukou.",
    slogan: "Stavba domu od projektu po prístupovú cestu — s jedným partnerom.",
    knowsAbout: [
      "Stavba rodinného domu na kľúč",
      "Holodom / hrubá stavba",
      "Zatepľovanie rodinných domov",
      "Zámková dlažba a prístupové cesty",
      "Nízkoenergetické domy",
      "Kompletný exteriérový balík",
    ],
    keywords:
      "dom na kľúč Bratislava, holodom, zateplenie fasády, zámková dlažba, prístupová cesta, kompletný exteriérový balík, stavba domu Podunajsko, rodinný dom",
    image: "https://supermajstri.sk/icon-512.png",
    logo: "https://supermajstri.sk/icon-512.png",
    url: "https://supermajstri.sk/",
    telephone: "+421907649964",
    email: "supermajstri.sk@gmail.com",
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Kopčianska 10",
      addressLocality: "Bratislava",
      addressRegion: "Bratislavský kraj",
      postalCode: "851 01",
      addressCountry: "SK",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "48.1486",
      longitude: "17.1077",
    },
    // Multi-location: Bratislava (sales/preview office) + Lehnice (registered
    // office listed on Google Business Profile). Both listed so NAP signals
    // match Google Maps and rich-result eligibility remains intact.
    location: [
      {
        "@type": "Place",
        "@id": "https://supermajstri.sk/#loc-bratislava",
        name: "SuperMajstri — Bratislava",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Kopčianska 10",
          addressLocality: "Bratislava",
          addressRegion: "Bratislavský kraj",
          postalCode: "851 01",
          addressCountry: "SK",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: "48.1486",
          longitude: "17.1077",
        },
      },
      {
        "@type": "Place",
        "@id": "https://supermajstri.sk/#loc-lehnice",
        name: "RESTOB s.r.o. — Lehnice",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Sása 403",
          addressLocality: "Lehnice",
          addressRegion: "Trnavský kraj",
          postalCode: "930 37",
          addressCountry: "SK",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: "48.117",
          longitude: "17.354",
        },
        hasMap: "https://maps.app.goo.gl/6PaRoCStWtNiQxtn9",
      },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "07:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "08:00",
        closes: "14:00",
      },
    ],
    areaServed: [
      // Primary service area — 60 km radius around Bratislava HQ
      {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: "48.1486",
          longitude: "17.1077",
        },
        geoRadius: "60000",
      },
      // Secondary service area — 60 km radius around Lehnice HQ (Podunajsko cluster)
      {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: "48.1361",
          longitude: "17.4986",
        },
        geoRadius: "60000",
      },
      // Administrative regions (kraje) we operate in
      { "@type": "AdministrativeArea", name: "Bratislavský kraj" },
      { "@type": "AdministrativeArea", name: "Trnavský kraj" },
      { "@type": "AdministrativeArea", name: "Nitriansky kraj" },
      // Explicit city list — LLM answer engines (ChatGPT/Gemini/Perplexity)
      // give extra weight to cities explicitly enumerated in JSON-LD.
      // Full Podunajsko cluster + Bratislavský kraj cities.
      { "@type": "City", name: "Bratislava" },
      { "@type": "City", name: "Senec" },
      { "@type": "City", name: "Dunajská Lužná" },
      { "@type": "City", name: "Rovinka" },
      { "@type": "City", name: "Ivanka pri Dunaji" },
      { "@type": "City", name: "Bernolákovo" },
      { "@type": "City", name: "Hviezdoslavov" },
      { "@type": "City", name: "Kvetoslavov" },
      { "@type": "City", name: "Miloslavov" },
      { "@type": "City", name: "Kalinkovo" },
      { "@type": "City", name: "Hamuliakovo" },
      { "@type": "City", name: "Malinovo" },
      { "@type": "City", name: "Tomášov" },
      { "@type": "City", name: "Zálesie" },
      { "@type": "City", name: "Hrubá Borša" },
      { "@type": "City", name: "Pezinok" },
      { "@type": "City", name: "Modra" },
      { "@type": "City", name: "Malacky" },
      { "@type": "City", name: "Stupava" },
      { "@type": "City", name: "Trnava" },
      { "@type": "City", name: "Dunajská Streda" },
      { "@type": "City", name: "Šamorín" },
      { "@type": "City", name: "Galanta" },
      { "@type": "City", name: "Sereď" },
      { "@type": "City", name: "Piešťany" },
      { "@type": "City", name: "Šaľa" },
      { "@type": "City", name: "Veľký Meder" },
      // Podunajsko obce (LLM hyperlokálny cluster)
      { "@type": "City", name: "Lehnice" },
      { "@type": "City", name: "Blahová" },
      { "@type": "City", name: "Horná Potôň" },
      { "@type": "City", name: "Benkova Potôň" },
      { "@type": "City", name: "Bellova Ves" },
      { "@type": "City", name: "Potônske Lúky" },
      { "@type": "City", name: "Oľdza" },
      { "@type": "City", name: "Mierovo" },
      { "@type": "City", name: "Janíky" },
      { "@type": "City", name: "Macov" },
      { "@type": "City", name: "Trnávka" },
      { "@type": "City", name: "Veľká Paka" },
      { "@type": "City", name: "Malá Paka" },
      { "@type": "City", name: "Orechová Potôň" },
      { "@type": "City", name: "Michal na Ostrove" },
      { "@type": "City", name: "Jelka" },
      { "@type": "City", name: "Eliášovce" },
      { "@type": "City", name: "Tonkovce" },
      { "@type": "City", name: "Sládkovičovo" },
      { "@type": "City", name: "Vrakuňa" },
      { "@type": "City", name: "Podunajské Biskupice" },
      { "@type": "City", name: "Devínska Nová Ves" },
    ],
    // Founder / owner — E-E-A-T signal (Expertise, Experience, Authority, Trust)
    // that LLM answer engines and Google specifically look for on service pages.
    founder: {
      "@type": "Person",
      "@id": "https://supermajstri.sk/#founder",
      name: "Roman Šoltés",
      jobTitle: "Konateľ a stavbyvedúci",
      worksFor: { "@id": "https://supermajstri.sk/#organization" },
      knowsAbout: [
        "Stavba rodinného domu na kľúč",
        "Výstavba holodomov",
        "Zatepľovanie fasád",
        "Zámková dlažba a prístupové cesty",
        "Stavebný manažment v Bratislavskom a Trnavskom kraji",
      ],
      hasCredential: {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Odborná spôsobilosť stavbyvedúceho",
        recognizedBy: { "@type": "Organization", name: "Slovenská komora stavebných inžinierov" },
      },
      description:
        "Roman Šoltés je konateľ a stavbyvedúci firmy RESTOB s.r.o. (SuperMajstri). Žije v Podunajsku a od roku 2009 riadi tím 22 murárov, fasádnej party a exteriérovej party. Osobne dohliada na každý projekt v okruhu 60 km od Lehníc.",
    },
    // aggregateRating & review arrays removed — Google penalises fake reviews.
    // Real Google Reviews are injected at runtime by
    // services/google_reviews.py once GOOGLE_PLACES_API_KEY + GOOGLE_PLACE_ID
    // are configured. Until then, no ratings/reviews are emitted here.
    sameAs: [
      "https://www.facebook.com/supermajstri.sk",
      "https://www.instagram.com/supermajstri",
      "https://maps.app.goo.gl/6PaRoCStWtNiQxtn9",
      "https://www.firmy.sk/hladaj?q=restob",
      "https://www.zoznam.sk/hladaj/?q=RESTOB+s.r.o.",
    ],
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Domov", item: "https://supermajstri.sk/" },
      { "@type": "ListItem", position: 2, name: "Služby", item: "https://supermajstri.sk/#sluzby" },
      { "@type": "ListItem", position: 3, name: "Cenník", item: "https://supermajstri.sk/#cennik" },
      { "@type": "ListItem", position: 4, name: "Kontakt", item: "https://supermajstri.sk/#kontakt" },
    ],
  };

  return [localBusiness, serviceCatalog, additionalCatalog, faq, breadcrumbs];
}
